import { useEffect, useMemo, useState } from 'react';
import type { AdminRosterResponse, AdminRosterRow, ClaimRequestsResponse, ClaimRequestRow } from '@mv/contracts';
import { api } from '../lib/api';
import Flag from './Flag';
import { IconUnlink } from './Icon';
import { fmtDay, now, relativeUploadTime } from '../lib/time';

/**
 * The organiser's roster controls (D59 §7).
 *
 * **This is more capability than an organiser holds anywhere else, and that is
 * deliberate rather than an oversight.** D37 and D50 kept the admin surface to
 * featuring and unfeaturing: reversible, non-destructive, and a wrong one is
 * undone by another organiser. Reassigning an identity is larger. It is here
 * because every failure path a guest cannot resolve alone ends at a human — a
 * name somebody else took, a plus-one not on the list, a row seeded with a name
 * spelled wrong — and without this the alternative for a stranded guest is
 * nothing at all.
 *
 * It is read-first and written rarely. The queue is the to-do list; the roster
 * below it answers two different questions, and it used to answer only one.
 *
 * **"Who has signed in?" is a survey, not a lookup.** The roster began as a
 * search box that rendered nothing until you typed, capped at twelve hits —
 * right for the guest standing in front of you, useless for the question an
 * organiser actually asks in the week before: who has claimed a name so far,
 * and under which address. So the whole list is shown by default, filterable by
 * claim state, with the email on every matched row rather than only as a
 * fallback for a guest who never set a display name.
 *
 * The search box stays, and while a queue entry is being matched it still gates
 * the list — assigning is a targeted act, and a hundred and fifty-nine
 * "give them this name" buttons is a mis-tap waiting to happen.
 */
export default function AdminRoster() {
  const [roster, setRoster] = useState<AdminRosterResponse | null>(null);
  const [requests, setRequests] = useState<ClaimRequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  /** Who we are matching a name to, while the search below picks which name. */
  const [assigning, setAssigning] = useState<ClaimRequestRow | null>(null);
  /**
   * Which slice of the roster is listed, **`none` by default**.
   *
   * It used to default to `all`, on the argument that "who has signed in" is a
   * survey rather than a lookup and a survey wants the whole list. That is
   * still true of the *question* and was wrong about the screen: a hundred and
   * fifty-nine cards render the moment the page opens, and everything above
   * them — the queue that is the actual to-do list, the counts, the search —
   * is one row of a long scroll. The controls were unreadable because of what
   * they controlled.
   *
   * So nothing is listed until something is asked for, and `none` is a real
   * choice rather than an initial value: the active chip toggles back to it,
   * which is the only way to put a 159-row list away again without reloading.
   * The counts stay visible in the chips throughout, so the survey's headline
   * answer costs no clicks at all.
   */
  const [filter, setFilter] = useState<'none' | 'all' | 'matched' | 'unmatched'>('none');

  useEffect(() => { void reload(); }, []);

  async function reload() {
    setError(null);
    try {
      const [r, c] = await Promise.all([
        api<AdminRosterResponse>('/admin/roster'),
        api<ClaimRequestsResponse>('/admin/claim-requests'),
      ]);
      setRoster(r); setRequests(c.requests);
    } catch {
      setError('Could not load the roster.');
    }
  }

  /**
   * Returns whether it worked, and callers must honour that.
   *
   * It used to swallow the failure and return nothing, so the assign handler's
   * `.then()` ran on failure exactly as on success — clearing the organiser's
   * in-progress match and the name they had typed. "Try again" then meant
   * re-opening the queue entry and re-typing a guest's name from scratch, while
   * standing in front of that guest, which is the one moment this control was
   * built for.
   */
  async function run(key: string, fn: () => Promise<unknown>): Promise<boolean> {
    setBusy(key); setError(null);
    try { await fn(); await reload(); return true; }
    catch { setError('That did not go through. Try again.'); return false; }
    finally { setBusy(null); }
  }

  const assign = (guestId: string, userId: string) =>
    run(guestId, () => api(`/admin/roster/${guestId}/assign`, { method: 'POST', json: { userId } }));
  const unclaim = (guestId: string) =>
    run(guestId, () => api(`/admin/roster/${guestId}/claim`, { method: 'DELETE' }));
  const dismiss = (userId: string) =>
    run(userId, () => api(`/admin/claim-requests/${userId}`, { method: 'DELETE' }));

  /**
   * Browsing shows everything; assigning still needs a query.
   *
   * The cap that used to sit here (`.slice(0, 12)`) was the other half of why
   * this could not answer "who has signed in" — even with a query, it stopped
   * at twelve. A hundred and fifty-nine rows of two lines each is a scroll, not
   * a performance problem, and the filter below is what makes it navigable.
   */
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (assigning && !needle) return [];
    return (roster?.rows ?? []).filter((r) => {
      // **Not while assigning**, and this is a correctness bug rather than a
      // tidiness one. The filter's own buttons are hidden in that mode, so a
      // filter left on "Not yet" would keep dropping every claimed row from a
      // picker whose entire purpose includes taking a name off whoever holds
      // it — "a name somebody else took" is the first case in this file's
      // docstring. The organiser types the disputed guest's name, gets
      // "Nobody on the roster matches that", and has no visible control
      // explaining why.
      if (!assigning && filter === 'matched' && !r.claimedBy) return false;
      if (!assigning && filter === 'unmatched' && r.claimedBy) return false;
      // `none` lists nothing on its own but must not disable the search — the
      // lookup case ("which row is this person?") is the other half of what
      // this screen is for, and a default that broke it would trade one
      // unusable state for another. A query overrides the chip; an empty one
      // under `none` is the quiet screen that is the point of it.
      if (!assigning && filter === 'none' && !needle) return false;
      if (!needle) return true;
      return r.fullName.toLowerCase().includes(needle)
        || (r.claimedByName ?? '').toLowerCase().includes(needle)
        || (r.claimedByEmail ?? '').toLowerCase().includes(needle);
    });
  }, [roster, q, filter, assigning]);

  return (
    <>
      <h3 style={{ margin: '16px 0 4px' }}>Roster</h3>
      {roster && (
        <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>
          {roster.claimed} of {roster.rows.length} matched · {roster.unclaimed} still to arrive
        </p>
      )}
      {error && <p role="alert" style={{ fontSize: 13, color: 'var(--color-accent-700)', marginTop: 12 }}>{error}</p>}

      {/* The queue first. It is the only part of this screen that is a to-do
          list — the roster below is looked up, not worked through. */}
      {requests && requests.length > 0 && (
        <>
          <h4 style={{ margin: '18px 0 6px' }}>Can't find themselves ({requests.length})</h4>
          <ul className="stack" style={{ listStyle: 'none', padding: 0, gap: 8 }}>
            {requests.map((r) => (
              <li key={r.userId} className="card" style={{ padding: 12 }}>
                <strong>{r.displayName || r.email}</strong>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{r.email}</div>
                {r.note && <p style={{ fontSize: 13, margin: '8px 0 0' }}>“{r.note}”</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button" className="btn" disabled={busy === r.userId}
                    onClick={() => { setQ(r.displayName || r.email); setAssigning(r); }}
                  >
                    Match to a name
                  </button>
                  <button type="button" className="btn" disabled={busy === r.userId} onClick={() => { void dismiss(r.userId); }}>
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Not shown while assigning: there the list is a picker for one name,
          and a claim-state filter over it would only hide the row you want. */}
      {roster && !assigning && (
        <div className="roster-filters" role="group" aria-label="Filter the roster">
          {([
            ['all', 'Everyone', roster.rows.length],
            ['matched', 'Signed in', roster.claimed],
            ['unmatched', 'Not yet', roster.unclaimed],
          ] as const).map(([key, label, count]) => (
            <button
              key={key} type="button"
              className={`btn btn-tiny${filter === key ? ' btn-primary' : ''}`}
              aria-pressed={filter === key}
              // Tapping the active chip puts the list away again. Without it
              // `none` would be reachable only by reloading the page, which
              // makes it a starting position rather than a state.
              onClick={() => setFilter(filter === key ? 'none' : key)}
            >
              {label} <span style={{ opacity: 0.7 }}>{count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="field roster-search">
        <label htmlFor="admin-roster-q">
          {assigning ? `Find the name for ${assigning.displayName || assigning.email}` : 'Search the roster'}
        </label>
        <input
          id="admin-roster-q" className="input" autoComplete="off"
          value={q} onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {assigning && !q.trim() && (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Type a name to pick the row to give them.</p>
      )}
      {!assigning && roster && shown.length === 0 && (
        <p style={{ fontSize: 13, opacity: 0.6 }}>
          {/* Three different empty screens. The first is the resting state and
              is not an emptiness at all — saying "nobody in this group yet"
              there, which is what this line used to do for every case, would
              report the roster as empty on a page whose own chips are showing
              159 of them. */}
          {q.trim()
            ? 'Nobody on the roster matches that.'
            : filter === 'none'
              ? 'Pick a group above to list names, or search for somebody.'
              : 'Nobody in this group yet.'}
        </p>
      )}

      {/* Two to a row while browsing, one while assigning. The survey is a
          density problem — 159 rows read standing up — and the picker is not:
          there the action button carries a sentence, and half a phone's width
          wraps it into a paragraph. */}
      <ul className={assigning ? 'stack roster-list' : 'roster-grid'}>
        {shown.map((r) => (
          <li key={r.guestId} className="card roster-card">
            <div className="roster-row-head">
              {/* The seeded team, which is the roster's own column rather than
                  anything an account carries. */}
              {r.team && <Flag team={r.team} height={14} decorative />}
              <strong>{r.fullName}</strong>
              {/* Top right of the card rather than a row beneath it. As a full
                  button it was the widest thing on the card and the only one
                  that moved the facts around — present on a matched row and
                  absent on an unmatched one, so two cards side by side put
                  their dates on different lines. An icon sits in space the
                  name is not using. It keeps a label and a tooltip: this takes
                  a name off somebody, and an unlabelled glyph is not a control
                  to do that from. */}
              {r.claimedBy && !assigning && (
                <button
                  type="button" className="btn-icon roster-unmatch"
                  disabled={busy === r.guestId}
                  aria-label={`Unmatch ${r.fullName}`}
                  title={`Unmatch ${r.fullName}`}
                  onClick={() => { void unclaim(r.guestId); }}
                >
                  <IconUnlink size={15} />
                </button>
              )}
            </div>
            <ClaimLine row={r} />
            <RosterFacts row={r} />
            <div className="roster-card-actions">
              {assigning && (
                <button
                  type="button" className="btn btn-primary" disabled={busy === r.guestId}
                  onClick={() => {
                    // Only cleared on success: a failure leaves the match and
                    // the typed name exactly where they were, so "try again"
                    // means tapping the same button.
                    void assign(r.guestId, assigning.userId)
                      .then((ok) => { if (ok) { setAssigning(null); setQ(''); } });
                  }}
                >
                  {/* Says what it will do, because on a claimed row it takes the
                      name off whoever holds it. An organiser resolving a dispute
                      should not discover that afterwards. */}
                  {r.claimedBy ? 'Take this name and give it over' : 'Give them this name'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {assigning && (
        <button type="button" className="btn btn-block" style={{ marginTop: 12 }} onClick={() => { setAssigning(null); setQ(''); }}>
          Cancel
        </button>
      )}
    </>
  );
}

/**
 * What an organiser reads off a roster row.
 *
 * **The email is unconditional on a matched row**, which is the change this
 * screen exists for. It used to render as `claimedByName || claimedByEmail`, so
 * the address — the one field that identifies which of somebody's two sign-ins
 * took the name, and the thing to read back to a guest on the phone — was
 * visible only for a guest who had never set a display name. Exactly backwards.
 *
 * `claimedBy` is the `sub`, shown only when the join found neither a name nor an
 * address. That means the guest row is gone while the roster row still points at
 * it, which is a repair an organiser can only make if they can see the id.
 */
function ClaimLine({ row }: { row: AdminRosterRow }) {
  if (!row.claimedBy) {
    return <div className="roster-claim roster-claim-open">Not signed in yet</div>;
  }

  const when = row.claimedAt;
  return (
    <div className="roster-claim">
      <div>{row.claimedByName || <em>no name set</em>}</div>
      {row.claimedByEmail
        ? <div className="roster-claim-email">{row.claimedByEmail}</div>
        : <div className="roster-claim-email"><code>{row.claimedBy}</code></div>}
      {when != null && (
        <div className="roster-claim-when" title={`${fmtDay(when)} Khao Lak time`}>
          {/* Relative while it is recent, because during the week "20 min ago"
              is what tells an organiser the guest is at the desk right now. */}
          {now() - when < 36 * 3600_000 ? relativeUploadTime(when) : fmtDay(when)}
        </div>
      )}
    </div>
  );
}

/**
 * The same three facts the guest sees on their own card, beside the name here.
 *
 * **Every row shows all three, and a missing one is a dash.** This rendered
 * nothing at all when the columns were empty — the argument being that four
 * placeholders on every card is the opposite of density, in a view whose whole
 * purpose is scanning 159 of them. Two things were wrong with it.
 *
 * The first is that it made the cards different heights depending on how far
 * through the spreadsheet somebody had got, so a grid of them stepped up and
 * down and nothing lined up across a row. The second is what an organiser is
 * actually looking for: this screen is used to find the gaps. A card that says
 * `Arrive —` is *the answer* to "whose flights am I still missing", and one
 * that renders nothing hides it.
 *
 * `household` is the exception and stays conditional. It is not one of the
 * three, it is the only field here about somebody other than this guest, and
 * there is no "who did they come with" gap worth hunting for.
 *
 * Values are printed as written — see `TripCard`, which makes the same call for
 * the same reason.
 */
const MISSING = '\u2014';

function RosterFacts({ row }: { row: AdminRosterRow }) {
  return (
    <dl className="roster-facts">
      {([
        ['Arrive', row.arrival],
        ['Depart', row.departure],
        ['Table', row.table],
        // Dropped entirely when empty, unlike the three above.
        ...(row.household ? [['With', row.household] as const] : []),
      ] as const).map(([k, raw]) => {
        // Collapsed to `undefined` once, so the dash and the dimmed class can
        // never disagree about what counts as missing. They were `??` and a
        // truthy test respectively, which agree on `undefined` and part company
        // on `''` — an empty string would have rendered a blank line wearing
        // the "missing" style instead of a dash. Unreachable today, because
        // `seed-roster.mjs` normalises empty CSV cells with `|| undefined` and
        // no route can write these fields, but the two conditions sitting one
        // line apart and meaning different things is the trap, not the value.
        const v = raw || undefined;
        return (
          <div key={k}>
            <dt>{k}</dt>
            <dd className={v ? undefined : 'roster-fact-missing'}>
              {v ?? <span aria-label="not set yet">{MISSING}</span>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
